import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';

const AuthContext = createContext(null);

// Normalize claims to roles array (handles both legacy single-role and new array format)
function extractRoles(claims) {
  if (Array.isArray(claims?.roles)) return claims.roles;
  if (claims?.role) return [claims.role];
  return [];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]); // e.g. ['reader', 'author']
  const [authorId, setAuthorId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refresh claims from the ID token
  const refreshClaims = useCallback(async (currentUser) => {
    if (!currentUser) {
      setRoles([]);
      setAuthorId(null);
      return;
    }
    try {
      const tokenResult = await getIdTokenResult(currentUser, true);
      const claims = tokenResult.claims;
      setRoles(extractRoles(claims));
      setAuthorId(claims.authorId || null);
    } catch {
      setRoles([]);
      setAuthorId(null);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await refreshClaims(firebaseUser);
      } else {
        setRoles([]);
        setAuthorId(null);
      }
      setIsLoading(false);
    });
    return unsub;
  }, [refreshClaims]);

  // Force refresh claims (call after setting new claims via Cloud Function)
  const forceRefreshClaims = useCallback(async () => {
    if (user) {
      await user.getIdToken(true);
      await refreshClaims(user);
    }
  }, [user, refreshClaims]);

  return (
    <AuthContext.Provider value={{
      user,
      roles,
      role: roles[0] || null, // backward compat — primary role
      authorId,
      isLoading,
      emailVerified: user?.emailVerified ?? false,
      isSuperadmin: roles.includes('superadmin'),
      isAuthor: roles.includes('author'),
      isReader: roles.includes('reader'),
      isGuest: roles.includes('guest'),
      hasMultipleRoles: roles.filter(r => r === 'author' || r === 'reader').length > 1,
      forceRefreshClaims,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
